// Top navigation bar — sits above the main app shell on /browse,
// hosts the search slot in the center, and surfaces the sign-out
// affordance on the right.
//
// Pass 3.8 cutover: the Browse/Upload nav-link cluster on the
// left is gone with the /upload route. /browse is now the only
// auth-gated app destination, so a nav switcher carries no
// information. The "Slack Archive Viewer" wordmark replaces it
// to keep the bar visually balanced.
//
// Pass 5.2: removed the "System status" link added in Phase 5.0
// — the Chooser now hosts System status as a first-class tile,
// so a TopNav shortcut is redundant. The wordmark + sign-out
// pair returns the bar to its pre-5.0 shape.

import type { ReactNode } from "react";
import { Link } from "react-router-dom";

import { SignOutButton } from "./SignOutButton";
import { useAuth, decodeJwtClaims } from "../state/AuthContext";
import { gradientCSSFor } from "../lib/avatarGradient";

interface TopNavProps {
  /** Pass 2.12: optional content rendered to the LEFT of the
   *  sign-out button. BrowseView fills this with ResetLayoutButton
   *  when the resizable layout is active. */
  rightSlot?: ReactNode;
  /** Pass 2.13: optional content rendered between the wordmark
   *  and the right slot. BrowseView fills this with the search
   *  bar (and its anchored results dropdown) when a workspace is
   *  active. The slot is positioned with position:relative so the
   *  SearchResults dropdown can absolutely-position itself
   *  relative to the bar. */
  centerSlot?: ReactNode;
}

export function TopNav({ rightSlot, centerSlot }: TopNavProps) {
  // Aurora: the signed-in user's gradient avatar bubble (Figma).
  const { token } = useAuth();
  const username = (decodeJwtClaims(token)?.sub as string | undefined) || "?";
  return (
    <header
      data-testid="top-nav"
      className="flex items-center justify-between px-6 py-3 border-b border-white/[0.07] bg-white/[0.03] backdrop-blur-xl flex-shrink-0 relative z-30"
    >
      {/* Aurora: the brand IS the home affordance — clicking the
          ✦ chip + wordmark goes back to the Home command center
          (replaces the old separate house-icon HomeButton). */}
      <Link
        to="/chooser"
        data-testid="topnav-home"
        aria-label="Back to home"
        title="Back to home"
        className="flex items-center gap-3 group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-theme-focus-ring rounded-lg"
      >
        <span
          aria-hidden
          style={{ background: "linear-gradient(135deg, #8b6cff, #6366f1)" }}
          className="w-7 h-7 rounded-[9px] hidden sm:flex items-center justify-center text-white text-sm font-bold shadow-[0_2px_10px_rgba(124,92,255,0.45)] group-hover:shadow-[0_2px_14px_rgba(124,92,255,0.7)] transition-shadow duration-150"
        >
          ✦
        </span>
        <span
          data-testid="topnav-wordmark"
          className="font-serif text-base font-semibold text-theme-text-primary tracking-tightish group-hover:text-theme-accent-link transition-colors duration-150"
        >
          Slack Archive Viewer
        </span>
      </Link>
      {centerSlot && (
        <div
          data-testid="top-nav-center"
          className="flex-1 flex justify-center px-4 relative"
        >
          {centerSlot}
        </div>
      )}
      <div className="flex items-center gap-3">
        {rightSlot}
        <SignOutButton />
        <span
          data-testid="topnav-avatar"
          aria-hidden
          title={username}
          style={{ background: gradientCSSFor(username) }}
          className="w-8 h-8 rounded-full flex items-center justify-center text-white text-[12px] font-semibold flex-shrink-0"
        >
          {username.slice(0, 1).toUpperCase()}
        </span>
      </div>
    </header>
  );
}
